import { Switch, Tooltip } from "@xola/ui-kit";
import { useFormikContext } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { MessageTemplate } from "types";
import EmailForm from "./EmailForm";
import { FormState } from "./MessageModalTypes";
import SmsForm from "./SmsForm";

interface MessageFormProps {
    template: MessageTemplate | null;
    isEmailAvailable?: boolean;
    isSmsAvailable?: boolean;
    canToggleEmailChannel?: boolean;
}

export const MessageForm = ({
    isEmailAvailable = true,
    isSmsAvailable = false,
    canToggleEmailChannel = true,
    template,
}: MessageFormProps) => {
    const { values, setFieldValue } = useFormikContext<FormState>();
    const [editTemplate, setEditTemplate] = useState<boolean>(false);

    const canShowSmsForm = useMemo(() => isSmsAvailable && !!template?.sms?.body, [template, isSmsAvailable]);
    const canShowEmailSwitch = useMemo(
        () => !isEmailAvailable || (isSmsAvailable && canToggleEmailChannel),
        [isEmailAvailable, isSmsAvailable, canToggleEmailChannel],
    );
    const canShowEmailForm = useMemo(() => isEmailAvailable && !!template?.email?.body, [isEmailAvailable, template]);

    useEffect(() => {
        if (template) {
            setFieldValue("enableSms", canShowSmsForm);
            setFieldValue("enableEmail", canShowEmailForm);
        }
    }, [template, canShowEmailForm, canShowSmsForm, setFieldValue]);

    return (
        <>
            <div>
                {/* No need to show Email switch if SMS channel is not enabled. Or if `canToggleEmailChannel` is false in config */}
                {canShowEmailSwitch ? (
                    <ChannelSwitch
                        label="Email"
                        disabled={!isEmailAvailable}
                        tooltip="No contact information available"
                        isChecked={values.enableEmail}
                        onChange={(checked) => setFieldValue("enableEmail", checked)}
                    />
                ) : (
                    <label className="mb-4 block text-md font-bold">Email</label>
                )}

                {template && <ChannelSwitch label="Edit Message" isChecked={editTemplate} onChange={setEditTemplate} />}

                {values.enableEmail && <EmailForm template={template} editTemplate={editTemplate} />}
            </div>

            {isSmsAvailable ? (
                <div className="mt-4">
                    <ChannelSwitch
                        label="SMS"
                        isChecked={values.enableSms}
                        onChange={(checked) => setFieldValue("enableSms", checked)}
                    />

                    {values.enableSms ? <SmsForm template={template} editTemplate={editTemplate} /> : null}
                </div>
            ) : null}
        </>
    );
};

interface ChannelSwitchProps {
    isChecked: boolean;
    label: string;
    onChange: (isChecked: boolean) => void;

    disabled?: boolean;

    /** Tooltip message needs to be shown when switch is disabled  */
    tooltip?: string;
}

export const ChannelSwitch = ({ label, isChecked, disabled, tooltip, onChange }: ChannelSwitchProps) => {
    return (
        <div className="mb-4 flex">
            <label className="flex-grow text-md font-bold">{label}</label>

            <Tooltip content={tooltip ?? ""} disabled={!disabled}>
                <Switch
                    data-testid="message-modal-toggle-button"
                    size="medium"
                    isChecked={isChecked}
                    disabled={disabled}
                    onChange={() => onChange(!isChecked)}
                />
            </Tooltip>
        </div>
    );
};
